/**
 * Flow editor — built-in camera poses.
 *
 * Each preset is a {position, rotation(euler)} pair, mirroring the SR editor's
 * model so every view (Default / Left / Right / Custom) is fully editable via
 * Pos/Rot sliders.
 *
 * These used to be derived from lookAt targets through a plain THREE.Object3D.
 * That was wrong: Object3D.lookAt aims the object's +Z at the target, while a
 * camera looks down -Z. Every built-in pose therefore faced 180° AWAY from the
 * track (and tilted the wrong way), so anyone without a saved override — or
 * anyone who pressed "Reset" — got a camera staring into empty space. The poses
 * are now literal, verified numbers: what you read here is what the camera does.
 */

export type Vec3 = [number, number, number];
export interface CameraPose { pos: Vec3; rot: Vec3; }

/**
 * The shipped Default view. Sits behind and above the strike line looking down
 * the runway (+Z), tilted ~18° down so the trigger line sits in the lower third
 * and the runway fills the frame. This is the framing signed off as "the
 * default for everyone" — the migration in flowStore forces it onto existing
 * installs, so change it only deliberately.
 */
const DEFAULT_POSE: CameraPose = { pos: [0, 3.1, -4], rot: [0.32, -3.14, 0] };

/** Built-in poses used whenever a view has no user override. */
export const BUILTIN_CAMERA: Record<'default' | 'left' | 'right' | 'custom', CameraPose> = {
  default: DEFAULT_POSE,
  // Side views: over the shoulder from behind-left / behind-right, angled back
  // in toward the strike line.
  left:    { pos: [-4.8, 2.8, -7.6], rot: [-0.08, -2.55, -0.04] },
  right:   { pos: [4.8, 2.8, -7.6],  rot: [-0.08, 2.55, 0.04] },
  // Custom starts life as the default pose until the user saves their own.
  custom:  DEFAULT_POSE,
};
